/** Application root — session handling and the app shell. */

import { useCallback, useEffect, useState } from "react";
import { LoginScreen } from "./modules/auth/LoginScreen";
import { AppShell } from "./modules/shell/AppShell";
import { api, getToken, setToken } from "./lib/api";
import type { LoginResponse, User } from "./lib/types";

type Session = { user: User; modules: string[] } | null;

export function App() {
  const [session, setSession] = useState<Session>(null);
  const [checking, setChecking] = useState(true);

  // Resume an existing session on load rather than making a supervisor sign in
  // again every time the tablet sleeps.
  useEffect(() => {
    if (!getToken()) {
      setChecking(false);
      return;
    }
    let alive = true;
    // `/session`, not `/me`: the module list is what scopes the navigation,
    // and rebuilding from `/me` alone left it empty — so a refresh silently
    // showed every screen to every role.
    api
      .session()
      .then(({ user, modules }) => {
        if (alive) setSession({ user, modules });
      })
      .catch(() => setToken(null))
      .finally(() => alive && setChecking(false));
    return () => {
      alive = false;
    };
  }, []);

  const signIn = useCallback((response: LoginResponse) => {
    setSession({ user: response.user, modules: response.modules });
  }, []);

  // A rename has to reach the top bar and the shell, not just the form that
  // made it — otherwise the person saves their name and still sees the old
  // one looking back at them.
  const updateUser = useCallback((user: User) => {
    setSession((current) => (current ? { ...current, user } : current));
  }, []);

  const signOut = useCallback(() => {
    api.logout();
    setSession(null);
  }, []);

  if (checking) {
    // A shimmer, never a full-page spinner (section 7.6).
    return (
      <div className="boot">
        <div className="skeleton boot__bar" />
      </div>
    );
  }

  if (!session) return <LoginScreen onSignedIn={signIn} />;

  return (
    <AppShell
      user={session.user}
      modules={session.modules}
      onSignOut={signOut}
      onUserChange={updateUser}
    />
  );
}
